import type { EncodingSystem } from "./encodingProfiles";
import type { UiLanguage } from "./uiMessages";

type CompactHelpMapping = {
  description: string;
  digit: string;
};

type CompactHelpDescriptor = {
  introduction: string;
  mapping: readonly CompactHelpMapping[];
  notes: string;
  title: string;
  wikipediaUrl: string;
};

type HelpFeatureImage = {
  alt: string;
  caption?: string;
  src: string;
};

type FullHelpFeature = {
  description: string;
  images: readonly HelpFeatureImage[];
  sample: string;
  title: string;
};

type EncodingHelpDescriptor = {
  compact: CompactHelpDescriptor;
  full: readonly FullHelpFeature[];
};

export const ENCODING_HELP_BY_SYSTEM = {
  english: {
    compact: {
      introduction: "Use spoken sounds, not spelling.",
      mapping: [
        { digit: "0", description: "Sounds /s/ and /z/. Letters s, z, and soft c." },
        { digit: "1", description: "Sounds /t/, /d/, and /th/. Letters t, d, and th." },
        { digit: "2", description: "Sound /n/. Letter n." },
        { digit: "3", description: "Sound /m/. Letter m." },
        { digit: "4", description: "Sound /r/. Letter r." },
        { digit: "5", description: "Sound /l/. Letter l." },
        { digit: "6", description: "Sounds /sh/, /ch/, /j/, and /zh/. Letters sh, ch, j, and soft g." },
        { digit: "7", description: "Sounds /k/ and hard /g/. Letters k, q, hard c, and hard g." },
        { digit: "8", description: "Sounds /f/ and /v/. Letters f, v, and ph." },
        { digit: "9", description: "Sounds /p/ and /b/. Letters p and b." },
      ],
      notes: "Vowels and the /h/, /w/, and /y/ sounds are not mapped. Add or ignore them freely. Silent letters do not count. A word with no mapped sound is only a filler word.",
      title: "Major system",
      wikipediaUrl: "https://en.wikipedia.org/wiki/Mnemonic_major_system",
    },
    full: [
      {
        description: "Enter one digit string per line. Major Code appears next to it.",
        images: [{
          alt: "Nummem encoding the speed of light in vacuum",
          src: "/assets/help/enter-numbers.png",
        }],
        sample: "Sample: speed of light in vacuum = 299792458 m/s.",
        title: "Encode numbers",
      },
      {
        description: "Optimal uses the fewest words. Random lets you play with different valid mappings.",
        images: [{
          alt: "Nummem Optimal and Random encoding selector with Pi digits",
          src: "/assets/help/encoding-mode.png",
        }],
        sample: "Sample: Pi = 3.141592653589793.",
        title: "Choose a mode",
      },
      {
        description: "Copy one exact mapping link. If sign-in is required, Nummem restores it automatically after Google authorization in the same tab.",
        images: [{
          alt: "Nummem share-mapping dialog",
          src: "/assets/help/share-mapping.png",
        }],
        sample: "Anyone with the complete link and Nummem access can read it, so do not use it for confidential numbers.",
        title: "Share a mapping",
      },
      {
        description: "Search a known value to load its exact digits and source details.",
        images: [{
          alt: "Nummem known-number search with the Battle of Marathon",
          src: "/assets/help/known-values.png",
        }],
        sample: "Sample: Battle of Marathon = 490 BC.",
        title: "Load known values",
      },
      {
        description: "Select the star to remember a mapping. Select it again to forget it.",
        images: [{
          alt: "Nummem saved Major Code for Euler's number",
          src: "/assets/help/save-mapping.png",
        }],
        sample: "Sample: Euler's number = 2.718281828459045.",
        title: "Save or forget a mapping",
      },
      {
        description: "Drag the divider to make either block wider.",
        images: [{
          alt: "Nummem resized Numbers and Major Code blocks",
          src: "/assets/help/resize-blocks.png",
        }],
        sample: "Sample: use a wider Major Code block for the speed of light.",
        title: "Resize the blocks",
      },
      {
        description: "Hover a known number or a Major Code word to see quick details.",
        images: [
          {
            alt: "Known-number hover card for the Planck constant",
            caption: "Value, selected part, and source.",
            src: "/assets/help/hover-known-number.png",
          },
          {
            alt: "Major Code word hover card for a Planck constant mapping",
            caption: "Number, pronunciation, and definition.",
            src: "/assets/help/hover-major-code.png",
          },
        ],
        sample: "Sample: inspect the Planck constant and one encoded word.",
        title: "Read hover details",
      },
      {
        description: "Open Account, then Data. Download every saved mapping as CSV.",
        images: [{
          alt: "Nummem Data tab with the saved mappings CSV export",
          src: "/assets/help/export-mappings.png",
        }],
        sample: "Sample: export your saved physical-constant mappings.",
        title: "Export saved mappings",
      },
    ],
  },
  spanish: {
    compact: {
      introduction: "Use spoken sounds, not spelling.",
      mapping: [
        { digit: "0", description: "Sound /s/. Letter s; z and soft c in seseo." },
        { digit: "1", description: "Sounds /t/, /d/, and /θ/. Letters t and d; z and soft c for /θ/." },
        { digit: "2", description: "Sounds /n/ and /ɲ/. Letters n and ñ." },
        { digit: "3", description: "Sound /m/. Letter m." },
        { digit: "4", description: "Sounds /r/ and /ɾ/. Letters r and rr." },
        { digit: "5", description: "Sounds /l/ and /ʎ/. Letters l and ll." },
        { digit: "6", description: "Sounds /tʃ/, /ʃ/, /ʒ/, and /ʝ/. Letters ch, y, ll, and sh." },
        { digit: "7", description: "Sounds /k/, /g/, and /x/. Letters k, q, c, g, and j." },
        { digit: "8", description: "Sounds /f/ and /v/. Letter f; v in some words." },
        { digit: "9", description: "Sounds /p/ and /b/. Letters p, b, and usually v." },
      ],
      notes: "Vowels and the /h/, /j/, and /w/ sounds are ignored. The documented pronunciation decides the digits.",
      title: "Spanish Major system",
      wikipediaUrl: "https://es.wikipedia.org/wiki/C%C3%B3digo_fon%C3%A9tico_(memorizaci%C3%B3n)",
    },
    full: [
      {
        description: "Spanish uses an independent IPA sound mapping, noun lexicon, validation rules, saved mappings, and Spanish Wikipedia content.",
        images: [{
          alt: "Nummem encoding-system selector",
          src: "/assets/help/encoding-system.png",
        }],
        sample: "Sample: mesa /ˈmesa/ = 30; Peninsular cena /ˈθena/ = 12.",
        title: "Choose an encoding system",
      },
      {
        description: "Enter one digit string per line. Spanish Major Code appears next to it.",
        images: [{ alt: "Nummem number entry", src: "/assets/help/enter-numbers.png" }],
        sample: "Sample: 30 can use mesa /ˈmesa/.",
        title: "Encode numbers",
      },
      {
        description: "Optimal uses the fewest Spanish nouns. Random lets you try other valid Spanish mappings.",
        images: [{ alt: "Nummem encoding-mode selector", src: "/assets/help/encoding-mode.png" }],
        sample: "Every result stays inside the Spanish profile.",
        title: "Choose a mode",
      },
      {
        description: "Copy one exact Spanish mapping link. If sign-in is required, Nummem restores the selected profile and mapping after Google authorization in the same tab.",
        images: [{ alt: "Nummem share-mapping dialog", src: "/assets/help/share-mapping.png" }],
        sample: "Anyone with the complete link and Nummem access can read it, so do not use it for confidential numbers.",
        title: "Share a mapping",
      },
      {
        description: "Search a known value to load its exact digits and source details. Its number preview uses Spanish Wikipedia content when available.",
        images: [{ alt: "Nummem known-value selector", src: "/assets/help/known-values.png" }],
        sample: "Spanish mode never falls back to English Wikipedia.",
        title: "Load known values",
      },
      {
        description: "Select the star to remember the exact Spanish nouns and pronunciation variants. Select it again to forget them.",
        images: [{ alt: "Nummem saved mapping", src: "/assets/help/save-mapping.png" }],
        sample: "Sample: save 30 = mesa with its documented Spanish IPA variant.",
        title: "Save or forget a mapping",
      },
      {
        description: "Drag the divider to make either block wider.",
        images: [{ alt: "Nummem resizable workbench", src: "/assets/help/resize-blocks.png" }],
        sample: "Use a wider Major Code block for a longer Spanish mapping.",
        title: "Resize the blocks",
      },
      {
        description: "Hover a known number or a Spanish Major Code noun to see profile-specific details.",
        images: [
          { alt: "Nummem known-number preview", src: "/assets/help/hover-known-number.png" },
          { alt: "Nummem word preview", src: "/assets/help/hover-major-code.png" },
        ],
        sample: "A noun card shows its digit chunk, Spanish IPA, dialect, definition, and Spanish Wikipedia page.",
        title: "Read hover details",
      },
      {
        description: "Open Account, then Data. Download the saved mappings for the selected Spanish profile as CSV.",
        images: [{ alt: "Nummem mapping export", src: "/assets/help/export-mappings.png" }],
        sample: "Spanish and English saved mappings stay separate.",
        title: "Export saved mappings",
      },
    ],
  },
  russian: {
    compact: {
      introduction: "Use Russian spelling directly; pronunciation is not needed.",
      mapping: [
        { digit: "0", description: "Letters с and з." },
        { digit: "1", description: "Letters т and д." },
        { digit: "2", description: "Letter н." },
        { digit: "3", description: "Letter м." },
        { digit: "4", description: "Letter р." },
        { digit: "5", description: "Letter л." },
        { digit: "6", description: "Letters ш, ж, ч, and щ." },
        { digit: "7", description: "Letters к, г, and х." },
        { digit: "8", description: "Letters ф and в." },
        { digit: "9", description: "Letters п and б." },
        { digit: "10", description: "Letter ц." },
      ],
      notes: "Russian vowels, й, ь, and ъ are ignored. Digits follow the normalized Cyrillic letters in source order.",
      title: "Russian Major system",
      wikipediaUrl: "https://ru.wikipedia.org/wiki/%D0%9C%D0%BD%D0%B5%D0%BC%D0%BE%D0%BD%D0%B8%D0%BA%D0%B0",
    },
    full: [
      {
        description: "Russian uses an independent Cyrillic letter mapping, Wikidata-only noun lexicon, validation rules, saved mappings, and Russian Wikipedia content.",
        images: [{
          alt: "Nummem encoding-system selector",
          src: "/assets/help/encoding-system.png",
        }],
        sample: "Sample: кошка = 767; машина = 362; цапля starts with 10.",
        title: "Choose an encoding system",
      },
      {
        description: "Enter one digit string per line. Russian Major Code appears next to it.",
        images: [{ alt: "Nummem number entry", src: "/assets/help/enter-numbers.png" }],
        sample: "Sample: 767 can use кошка.",
        title: "Encode numbers",
      },
      {
        description: "Optimal uses the fewest Russian nouns. Random lets you try other valid Russian mappings.",
        images: [{ alt: "Nummem encoding-mode selector", src: "/assets/help/encoding-mode.png" }],
        sample: "Every result stays inside the Russian profile.",
        title: "Choose a mode",
      },
      {
        description: "Copy one exact Russian mapping link. The link restores the selected profile and words.",
        images: [{ alt: "Nummem share-mapping dialog", src: "/assets/help/share-mapping.png" }],
        sample: "Do not use a shared link for confidential numbers.",
        title: "Share a mapping",
      },
      {
        description: "Search a known value to load its digits. Its preview resolves the Russian Wikipedia sitelink through Wikidata.",
        images: [{ alt: "Nummem known-value selector", src: "/assets/help/known-values.png" }],
        sample: "Russian mode never falls back to English Wikipedia.",
        title: "Load known values",
      },
      {
        description: "Select the star to remember the exact Russian nouns. Select it again to forget them.",
        images: [{ alt: "Nummem saved mapping", src: "/assets/help/save-mapping.png" }],
        sample: "Russian and other-language saved mappings stay separate.",
        title: "Save or forget a mapping",
      },
      {
        description: "Drag the divider to make either block wider.",
        images: [{ alt: "Nummem resizable workbench", src: "/assets/help/resize-blocks.png" }],
        sample: "Use a wider Major Code block for a longer Russian mapping.",
        title: "Resize the blocks",
      },
      {
        description: "Hover a known number or Russian noun to see its digit chunk, definition, category, and Russian Wikipedia page.",
        images: [
          { alt: "Nummem known-number preview", src: "/assets/help/hover-known-number.png" },
          { alt: "Nummem word preview", src: "/assets/help/hover-major-code.png" },
        ],
        sample: "No pronunciation is required or displayed for Russian words.",
        title: "Read hover details",
      },
      {
        description: "Open Account, then Data. Download saved mappings for the selected Russian profile as CSV.",
        images: [{ alt: "Nummem mapping export", src: "/assets/help/export-mappings.png" }],
        sample: "Russian, English, and Spanish exports stay separate.",
        title: "Export saved mappings",
      },
    ],
  },
} as const satisfies Record<EncodingSystem, EncodingHelpDescriptor>;

const LOCALIZED_HELP_CHROME: Record<
  Exclude<UiLanguage, "en">,
  Record<EncodingSystem, {
    introduction: string;
    mappingDescriptions: readonly string[];
    notes: string;
    title: string;
    featureDescriptions: readonly string[];
    featureSamples: readonly string[];
    featureTitles: readonly string[];
  }>
> = {
  ru: {
    english: {
      introduction: "Используйте произносимые звуки, а не написание.",
      mappingDescriptions: [
        "Звуки /s/ и /z/. Буквы s, z и c перед e, i, y.",
        "Звуки /t/, /d/ и /th/. Буквы t, d и сочетание th.",
        "Звук /n/. Буква n.",
        "Звук /m/. Буква m.",
        "Звук /r/. Буква r.",
        "Звук /l/. Буква l.",
        "Звуки /sh/, /ch/, /j/ и /zh/. Сочетания sh, ch, буква j и g перед e, i, y.",
        "Звуки /k/ и твердого /g/. Буквы k, q, твердые c и g.",
        "Звуки /f/ и /v/. Буквы f, v и сочетание ph.",
        "Звуки /p/ и /b/. Буквы p и b.",
      ],
      notes: "Гласные и звуки /h/, /w/ и /y/ не кодируются. Их можно свободно добавлять или игнорировать. Немую букву не учитывайте. Слово без кодируемого звука служит только заполнителем.",
      title: "Система Major",
      featureDescriptions: [
        "Введите одну строку цифр на каждой строке. Рядом появится код Major.",
        "Оптимальный режим использует наименьшее количество слов. Случайный режим позволяет пробовать разные допустимые сопоставления.",
        "Скопируйте ссылку на одно точное сопоставление. Если требуется вход, Nummem автоматически восстановит его после авторизации Google в той же вкладке.",
        "Найдите известное значение, чтобы загрузить его точные цифры и сведения об источнике.",
        "Нажмите на звезду, чтобы запомнить сопоставление. Нажмите еще раз, чтобы удалить его.",
        "Перетащите разделитель, чтобы сделать любой из блоков шире.",
        "Наведите указатель на известное число или слово кода Major, чтобы увидеть краткие сведения.",
        "Откройте Аккаунт, затем Данные. Скачайте все сохраненные сопоставления в формате CSV.",
      ],
      featureSamples: [
        "Пример: скорость света в вакууме = 299792458 м/с.",
        "Пример: число пи = 3.141592653589793.",
        "Любой пользователь с полной ссылкой и доступом к Nummem сможет прочитать ее, поэтому не используйте ссылку для конфиденциальных чисел.",
        "Пример: Марафонская битва = 490 год до н. э.",
        "Пример: число Эйлера = 2.718281828459045.",
        "Пример: расширьте блок кода Major для скорости света.",
        "Пример: изучите постоянную Планка и одно закодированное слово.",
        "Пример: экспортируйте сохраненные сопоставления физических констант.",
      ],
      featureTitles: ["Кодировать числа", "Выбрать режим", "Поделиться сопоставлением", "Загрузить известные значения", "Сохранить или удалить сопоставление", "Изменить размер блоков", "Читать сведения при наведении", "Экспортировать сохраненные сопоставления"],
    },
    russian: {
      introduction: "Используйте русское написание напрямую; произношение не требуется.",
      mappingDescriptions: [
        "Буквы с и з.",
        "Буквы т и д.",
        "Буква н.",
        "Буква м.",
        "Буква р.",
        "Буква л.",
        "Буквы ш, ж, ч и щ.",
        "Буквы к, г и х.",
        "Буквы ф и в.",
        "Буквы п и б.",
        "Буква ц.",
      ],
      notes: "Русские гласные, й, ь и ъ игнорируются. Цифры следуют за нормализованными кириллическими буквами в исходном порядке.",
      title: "Русская система Major",
      featureDescriptions: [
        "Русская система использует независимое сопоставление кириллических букв, словарь существительных только из Wikidata, собственные правила проверки, сохраненные сопоставления и материалы русской Википедии.",
        "Введите одну строку цифр на каждой строке. Рядом появится русский код Major.",
        "Оптимальный режим использует наименьшее количество русских существительных. Случайный режим позволяет пробовать другие допустимые русские сопоставления.",
        "Скопируйте ссылку на одно точное русское сопоставление. Ссылка восстановит выбранный профиль и слова.",
        "Найдите известное значение, чтобы загрузить его цифры. Предпросмотр находит ссылку на русскую Википедию через Wikidata.",
        "Нажмите на звезду, чтобы запомнить точные русские существительные. Нажмите еще раз, чтобы удалить их.",
        "Перетащите разделитель, чтобы сделать любой из блоков шире.",
        "Наведите указатель на известное число или русское существительное, чтобы увидеть его цифровой фрагмент, определение, категорию и страницу русской Википедии.",
        "Откройте Аккаунт, затем Данные. Скачайте сохраненные сопоставления выбранного русского профиля в формате CSV.",
      ],
      featureSamples: [
        "Пример: кошка = 767; машина = 362; цапля начинается с 10.",
        "Пример: для 767 можно использовать слово кошка.",
        "Каждый результат остается внутри русского профиля.",
        "Не используйте общую ссылку для конфиденциальных чисел.",
        "Русский режим никогда не переходит к английской Википедии.",
        "Сохраненные сопоставления русского и других языковых профилей хранятся раздельно.",
        "Для длинного русского сопоставления расширьте блок кода Major.",
        "Для русских слов произношение не требуется и не отображается.",
        "Экспорт для русского, английского и испанского профилей хранится раздельно.",
      ],
      featureTitles: ["Выбрать систему кодирования", "Кодировать числа", "Выбрать режим", "Поделиться сопоставлением", "Загрузить известные значения", "Сохранить или удалить сопоставление", "Изменить размер блоков", "Читать сведения при наведении", "Экспортировать сохраненные сопоставления"],
    },
    spanish: {
      introduction: "Используйте произносимые звуки, а не написание.",
      mappingDescriptions: [
        "Звук /s/. Буква s; z и c перед e, i при seseo.",
        "Звуки /t/, /d/ и /θ/. Буквы t и d; z и c перед e, i для /θ/.",
        "Звуки /n/ и /ɲ/. Буквы n и ñ.",
        "Звук /m/. Буква m.",
        "Звуки /r/ и /ɾ/. Буквы r и сочетание rr.",
        "Звуки /l/ и /ʎ/. Буква l и сочетание ll.",
        "Звуки /tʃ/, /ʃ/, /ʒ/ и /ʝ/. Сочетания ch, ll, sh и буква y.",
        "Звуки /k/, /g/ и /x/. Буквы k, q, c, g и j.",
        "Звуки /f/ и /v/. Буква f; в некоторых словах v.",
        "Звуки /p/ и /b/. Буквы p, b и обычно v.",
      ],
      notes: "Гласные и звуки /h/, /j/ и /w/ игнорируются. Цифры определяет документированное произношение.",
      title: "Испанская система Major",
      featureDescriptions: [
        "Испанская система использует независимое сопоставление звуков IPA, словарь существительных, собственные правила проверки, сохраненные сопоставления и материалы испанской Википедии.",
        "Введите одну строку цифр на каждой строке. Рядом появится испанский код Major.",
        "Оптимальный режим использует наименьшее количество испанских существительных. Случайный режим позволяет пробовать другие допустимые испанские сопоставления.",
        "Скопируйте ссылку на одно точное испанское сопоставление. Если требуется вход, Nummem восстановит выбранный профиль и сопоставление после авторизации Google в той же вкладке.",
        "Найдите известное значение, чтобы загрузить его точные цифры и сведения об источнике. Для предпросмотра числа используется испанская Википедия, когда материал доступен.",
        "Нажмите на звезду, чтобы запомнить точные испанские существительные и варианты произношения. Нажмите еще раз, чтобы удалить их.",
        "Перетащите разделитель, чтобы сделать любой из блоков шире.",
        "Наведите указатель на известное число или испанское существительное кода Major, чтобы увидеть сведения выбранного профиля.",
        "Откройте Аккаунт, затем Данные. Скачайте сохраненные сопоставления выбранного испанского профиля в формате CSV.",
      ],
      featureSamples: [
        "Пример: mesa /ˈmesa/ = 30; в пиренейском варианте cena /ˈθena/ = 12.",
        "Пример: для 30 можно использовать mesa /ˈmesa/.",
        "Каждый результат остается внутри испанского профиля.",
        "Любой пользователь с полной ссылкой и доступом к Nummem сможет прочитать ее, поэтому не используйте ссылку для конфиденциальных чисел.",
        "Испанский режим никогда не переходит к английской Википедии.",
        "Пример: сохраните 30 = mesa вместе с документированным вариантом испанского IPA.",
        "Для длинного испанского сопоставления расширьте блок кода Major.",
        "Карточка существительного показывает цифровой фрагмент, испанское произношение IPA, диалект, определение и страницу испанской Википедии.",
        "Сопоставления испанского и английского профилей хранятся раздельно.",
      ],
      featureTitles: ["Выбрать систему кодирования", "Кодировать числа", "Выбрать режим", "Поделиться сопоставлением", "Загрузить известные значения", "Сохранить или удалить сопоставление", "Изменить размер блоков", "Читать сведения при наведении", "Экспортировать сохраненные сопоставления"],
    },
  },
  es: {
    english: {
      introduction: "Usa los sonidos pronunciados, no la ortografía.",
      mappingDescriptions: [
        "Sonidos /s/ y /z/. Letras s, z y c suave.",
        "Sonidos /t/, /d/ y /th/. Letras t, d y la combinación th.",
        "Sonido /n/. Letra n.",
        "Sonido /m/. Letra m.",
        "Sonido /r/. Letra r.",
        "Sonido /l/. Letra l.",
        "Sonidos /sh/, /ch/, /j/ y /zh/. Combinaciones sh, ch, letra j y g suave.",
        "Sonidos /k/ y /g/ fuerte. Letras k, q, c fuerte y g fuerte.",
        "Sonidos /f/ y /v/. Letras f, v y la combinación ph.",
        "Sonidos /p/ y /b/. Letras p y b.",
      ],
      notes: "Las vocales y los sonidos /h/, /w/ e /y/ no se asocian. Puedes añadirlos o ignorarlos libremente. Las letras mudas no cuentan. Una palabra sin sonidos asociados solo sirve de relleno.",
      title: "Sistema Major",
      featureDescriptions: [
        "Introduce una cadena de dígitos por línea. El código Major aparece a su lado.",
        "Óptimo usa el menor número de palabras. Aleatorio permite probar distintas correspondencias válidas.",
        "Copia el enlace de una correspondencia exacta. Si es necesario iniciar sesión, Nummem la restaura automáticamente después de la autorización de Google en la misma pestaña.",
        "Busca un valor conocido para cargar sus dígitos exactos y los datos de la fuente.",
        "Selecciona la estrella para recordar una correspondencia. Selecciónala de nuevo para olvidarla.",
        "Arrastra el separador para ampliar cualquiera de los bloques.",
        "Pasa el puntero sobre un número conocido o una palabra del código Major para ver detalles breves.",
        "Abre Cuenta y después Datos. Descarga todas las correspondencias guardadas en formato CSV.",
      ],
      featureSamples: [
        "Ejemplo: velocidad de la luz en el vacío = 299792458 m/s.",
        "Ejemplo: pi = 3.141592653589793.",
        "Cualquier persona que tenga el enlace completo y acceso a Nummem puede leerlo; no lo uses para números confidenciales.",
        "Ejemplo: batalla de Maratón = 490 a. C.",
        "Ejemplo: número de Euler = 2.718281828459045.",
        "Ejemplo: amplía el bloque Código Major para la velocidad de la luz.",
        "Ejemplo: examina la constante de Planck y una palabra codificada.",
        "Ejemplo: exporta tus correspondencias guardadas de constantes físicas.",
      ],
      featureTitles: ["Codificar números", "Elegir un modo", "Compartir una correspondencia", "Cargar valores conocidos", "Guardar u olvidar una correspondencia", "Cambiar el tamaño de los bloques", "Leer detalles al pasar el puntero", "Exportar correspondencias guardadas"],
    },
    russian: {
      introduction: "Usa directamente la ortografía rusa; no se necesita pronunciación.",
      mappingDescriptions: [
        "Letras с y з.",
        "Letras т y д.",
        "Letra н.",
        "Letra м.",
        "Letra р.",
        "Letra л.",
        "Letras ш, ж, ч y щ.",
        "Letras к, г y х.",
        "Letras ф y в.",
        "Letras п y б.",
        "Letra ц.",
      ],
      notes: "Se ignoran las vocales rusas, й, ь y ъ. Los dígitos siguen las letras cirílicas normalizadas en el orden original.",
      title: "Sistema Major ruso",
      featureDescriptions: [
        "El ruso usa una correspondencia independiente de letras cirílicas, un léxico de sustantivos basado solo en Wikidata, reglas de validación propias, correspondencias guardadas y contenido de Wikipedia en ruso.",
        "Introduce una cadena de dígitos por línea. El código Major ruso aparece a su lado.",
        "Óptimo usa el menor número de sustantivos rusos. Aleatorio permite probar otras correspondencias rusas válidas.",
        "Copia el enlace de una correspondencia rusa exacta. El enlace restaura el perfil y las palabras seleccionados.",
        "Busca un valor conocido para cargar sus dígitos. La vista previa resuelve el enlace de Wikipedia en ruso mediante Wikidata.",
        "Selecciona la estrella para recordar los sustantivos rusos exactos. Selecciónala de nuevo para olvidarlos.",
        "Arrastra el separador para ampliar cualquiera de los bloques.",
        "Pasa el puntero sobre un número conocido o un sustantivo ruso para ver su fragmento numérico, definición, categoría y página de Wikipedia en ruso.",
        "Abre Cuenta y después Datos. Descarga en formato CSV las correspondencias guardadas del perfil ruso seleccionado.",
      ],
      featureSamples: [
        "Ejemplo: кошка = 767; машина = 362; цапля empieza por 10.",
        "Ejemplo: 767 puede usar кошка.",
        "Cada resultado permanece dentro del perfil ruso.",
        "No uses un enlace compartido para números confidenciales.",
        "El modo ruso nunca recurre a Wikipedia en inglés.",
        "Las correspondencias guardadas del perfil ruso y de los demás perfiles lingüísticos se mantienen separadas.",
        "Amplía el bloque Código Major para una correspondencia rusa larga.",
        "Las palabras rusas no necesitan ni muestran pronunciación.",
        "Las exportaciones de los perfiles ruso, inglés y español se mantienen separadas.",
      ],
      featureTitles: ["Elegir un sistema de codificación", "Codificar números", "Elegir un modo", "Compartir una correspondencia", "Cargar valores conocidos", "Guardar u olvidar una correspondencia", "Cambiar el tamaño de los bloques", "Leer detalles al pasar el puntero", "Exportar correspondencias guardadas"],
    },
    spanish: {
      introduction: "Usa los sonidos pronunciados, no la ortografía.",
      mappingDescriptions: [
        "Sonido /s/. Letra s; z y c suave con seseo.",
        "Sonidos /t/, /d/ y /θ/. Letras t y d; z y c suave para /θ/.",
        "Sonidos /n/ y /ɲ/. Letras n y ñ.",
        "Sonido /m/. Letra m.",
        "Sonidos /r/ y /ɾ/. Letras r y rr.",
        "Sonidos /l/ y /ʎ/. Letras l y ll.",
        "Sonidos /tʃ/, /ʃ/, /ʒ/ y /ʝ/. Letras ch, y, ll y sh.",
        "Sonidos /k/, /g/ y /x/. Letras k, q, c, g y j.",
        "Sonidos /f/ y /v/. Letra f; v en algunas palabras.",
        "Sonidos /p/ y /b/. Letras p, b y normalmente v.",
      ],
      notes: "Se ignoran las vocales y los sonidos /h/, /j/ y /w/. La pronunciación documentada determina los dígitos.",
      title: "Sistema Major español",
      featureDescriptions: [
        "El español usa una correspondencia fonética IPA independiente, un léxico de sustantivos, reglas de validación, correspondencias guardadas y contenido de Wikipedia en español.",
        "Introduce una cadena de dígitos por línea. El código Major español aparece a su lado.",
        "Óptimo usa el menor número de sustantivos españoles. Aleatorio permite probar otras correspondencias españolas válidas.",
        "Copia el enlace de una correspondencia española exacta. Si es necesario iniciar sesión, Nummem restaura el perfil y la correspondencia seleccionados después de la autorización de Google en la misma pestaña.",
        "Busca un valor conocido para cargar sus dígitos exactos y los datos de la fuente. La vista previa del número usa contenido de Wikipedia en español cuando está disponible.",
        "Selecciona la estrella para recordar los sustantivos españoles exactos y sus variantes de pronunciación. Selecciónala de nuevo para olvidarlos.",
        "Arrastra el separador para ampliar cualquiera de los bloques.",
        "Pasa el puntero sobre un número conocido o un sustantivo español del código Major para ver los datos específicos del perfil.",
        "Abre Cuenta y después Datos. Descarga en formato CSV las correspondencias guardadas del perfil español seleccionado.",
      ],
      featureSamples: [
        "Ejemplo: mesa /ˈmesa/ = 30; cena peninsular /ˈθena/ = 12.",
        "Ejemplo: 30 puede usar mesa /ˈmesa/.",
        "Cada resultado permanece dentro del perfil español.",
        "Cualquier persona que tenga el enlace completo y acceso a Nummem puede leerlo; no lo uses para números confidenciales.",
        "El modo español nunca recurre a Wikipedia en inglés.",
        "Ejemplo: guarda 30 = mesa con su variante documentada de IPA español.",
        "Amplía el bloque Código Major para una correspondencia española larga.",
        "La tarjeta de un sustantivo muestra su fragmento numérico, IPA español, dialecto, definición y página de Wikipedia en español.",
        "Las correspondencias guardadas de los perfiles español e inglés se mantienen separadas.",
      ],
      featureTitles: ["Elegir un sistema de codificación", "Codificar números", "Elegir un modo", "Compartir una correspondencia", "Cargar valores conocidos", "Guardar u olvidar una correspondencia", "Cambiar el tamaño de los bloques", "Leer detalles al pasar el puntero", "Exportar correspondencias guardadas"],
    },
  },
};

const LOCALIZED_SCREENSHOT_LABEL: Record<Exclude<UiLanguage, "en">, string> = {
  ru: "Снимок экрана Nummem",
  es: "Captura de pantalla de Nummem",
};

const LOCALIZED_IMAGE_CAPTIONS: Record<
  Exclude<UiLanguage, "en">,
  Record<string, string>
> = {
  ru: {
    "Value, selected part, and source.": "Значение, выбранная часть и источник.",
    "Number, pronunciation, and definition.": "Число, произношение и определение.",
  },
  es: {
    "Value, selected part, and source.": "Valor, parte seleccionada y fuente.",
    "Number, pronunciation, and definition.": "Número, pronunciación y definición.",
  },
};

export function getEncodingHelp(
  encodingSystem: EncodingSystem,
  uiLanguage: UiLanguage = "en",
): EncodingHelpDescriptor {
  const source = ENCODING_HELP_BY_SYSTEM[encodingSystem];
  if (uiLanguage === "en") return source;
  const localized = LOCALIZED_HELP_CHROME[uiLanguage][encodingSystem];
  return {
    compact: {
      ...source.compact,
      introduction: localized.introduction,
      mapping: source.compact.mapping.map((item, index) => ({
        ...item,
        description: localized.mappingDescriptions[index] ?? item.description,
      })),
      notes: localized.notes,
      title: localized.title,
    },
    full: source.full.map((feature, index) => {
      const title = localized.featureTitles[index] ?? feature.title;
      return {
        ...feature,
        description: localized.featureDescriptions[index] ?? feature.description,
        images: feature.images.map((image, imageIndex) => ({
          ...image,
          alt: `${LOCALIZED_SCREENSHOT_LABEL[uiLanguage]}: ${title}${
            feature.images.length > 1 ? ` ${imageIndex + 1}` : ""
          }`,
          ...("caption" in image && image.caption
            ? {
                caption: LOCALIZED_IMAGE_CAPTIONS[uiLanguage][image.caption] ??
                  image.caption,
              }
            : {}),
        })),
        sample: localized.featureSamples[index] ?? feature.sample,
        title,
      };
    }),
  };
}

export function spanishHelpFeatures(): readonly FullHelpFeature[] {
  return getEncodingHelp("spanish").full;
}
